import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore.js';
import { alEntrarAComercio } from '../lib/comercio.js';
import { esComandera } from '../store/deviceModeStore.js';
import { login, lookupTenants } from '../api/auth.api.js';
import { useHubStore } from '../lib/hub.js';
import client from '../api/client.js';
import styles from './LoginScreen.module.css';

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

const StoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9h18l-1.5-5.5A1 1 0 0 0 18.5 3h-13a1 1 0 0 0-1 .5L3 9Z" />
    <path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

// Por qué un correo no tiene dónde entrar (lo dice el servidor). Antes era un
// solo "No hay ninguna organización con POS activo" para cuatro causas.
const MOTIVOS = {
  sin_cuenta:         'No hay ninguna cuenta con ese correo. Revisa que esté bien escrito.',
  sin_activar:        'Tu cuenta todavía no está activada: abre el correo de invitación y crea tu contraseña.',
  plan_sin_pos:       'El plan de tu negocio no incluye punto de venta. Se cambia en el panel web, en Configuración › Suscripción.',
  sin_punto_de_venta: 'Tu negocio todavía no tiene punto de venta. Se crea en el panel web, en Canales › Crear punto de venta.',
};

export default function LoginScreen() {
  const navigate        = useNavigate();
  const hubUrl          = useHubStore((s) => s.hubUrl);
  const hubName         = useHubStore((s) => s.hubName);
  const loginStore      = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) navigate(esComandera() ? '/tables' : '/open-session', { replace: true });
  }, [isAuthenticated]);

  // step: 'email' | 'tenant' | 'password'
  const [step,     setStep]     = useState('email');
  const [email,    setEmail]    = useState('');
  const [tenants,  setTenants]  = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const selectedTenant = tenants.find((t) => t._id === tenantId) ?? null;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await lookupTenants(email);
      const list = res.data ?? [];
      if (!list.length) {
        setError(MOTIVOS[res.motivo] ?? MOTIVOS.sin_punto_de_venta);
        return;
      }
      setTenants(list);
      // Con varias organizaciones NO auto-seleccionamos: el cajero debe elegir
      // explícitamente para no entrar al tenant equivocado (uno sin POS).
      if (list.length === 1) {
        setTenantId(list[0]._id);
        setStep('password');
      } else {
        setTenantId('');
        setStep('tenant');
      }
    } catch {
      setError(navigator.onLine === false
        ? 'Este aparato no tiene internet. Conéctalo al WiFi e intenta de nuevo.'
        : 'No se pudo conectar con el servidor. Revisa el internet e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTenant = (id) => {
    setTenantId(id);
    setError(null);
    setStep('password');
  };

  const resetToEmail = () => {
    setStep('email');
    setTenants([]);
    setTenantId('');
    setPassword('');
    setError(null);
  };

  const emailIdentity = (
    <div className={styles.identity}>
      <span className={styles.identityIcon}><MailIcon /></span>
      <span className={styles.identityEmail}>{email}</span>
      <button type="button" className={styles.identityChange} onClick={resetToEmail}>
        Cambiar
      </button>
    </div>
  );

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(email, password, tenantId);
      const hasPos = data.data.tenant?.planLimits?.features?.hasPos;
      if (!hasPos) {
        setError('El plan de esta organización no incluye punto de venta. Cámbialo en el panel web, en Configuración → Suscripción.');
        return;
      }
      // Si este aparato venía de otro comercio, suelta lo que era de aquel.
      alEntrarAComercio(data.data.user.tenant_id);
      loginStore({
        token:    data.data.token,
        user:     data.data.user,
        tenantId: data.data.user.tenant_id,
        // El giro decide si esta caja muestra mesas y cocina. Viene del login
        // para no pedir otra vuelta al servidor antes de pintar el catálogo.
        tenant:   data.data.tenant ?? null,
      });
      navigate(esComandera() ? '/tables' : '/open-session', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.logoWrap}>
        <img src="/geek-commerce-light.svg" alt="Geek Commerce" className={styles.logoImg} />
        <span className={styles.logoBadge}>POS</span>
      </div>
      <div className={styles.card}>

        <h1 className={styles.title}>Iniciar sesión</h1>
        <p className={styles.sub}>Entra con el correo de tu cuenta</p>

        {step === 'email' && (
          <form className={styles.form} onSubmit={handleEmailSubmit}>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input
                className="input"
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <button
              className="btn btn--primary btn--full"
              type="submit"
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : 'Continuar'}
            </button>

            {import.meta.env.DEV && (
              <button
                type="button"
                className="btn btn--full"
                style={{ marginTop: 8, opacity: 0.6, fontSize: 12 }}
                onClick={async () => {
                  try {
                    const res = await client.post('/dev/token', { email: 'test@geekvibes.agency' });
                    const { token, user } = res.data.data;
                    alEntrarAComercio(user.tenant_id);
                    loginStore({ token, user, tenantId: user.tenant_id, tenant: res.data.data.tenant ?? null });
                    navigate(esComandera() ? '/tables' : '/open-session', { replace: true });
                  } catch (err) {
                    setError(err.response?.data?.error ?? 'Error al iniciar sesión dev');
                  }
                }}
              >
                [DEV] Quick Login
              </button>
            )}
          </form>
        )}

        {step === 'tenant' && (
          <div className={styles.form}>
            {emailIdentity}

            <div className={styles.field}>
              <label className={styles.label}>Elige tu organización</label>
              <div className={styles.tenantList}>
                {tenants.map((t) => (
                  <button
                    key={t._id}
                    type="button"
                    className={styles.tenantBtn}
                    onClick={() => handleSelectTenant(t._id)}
                  >
                    <span>{t.name}</span>
                    <span className={styles.tenantBtnChevron}>›</span>
                  </button>
                ))}
              </div>
            </div>

            {error && <p className={styles.error}>{error}</p>}
          </div>
        )}

        {step === 'password' && (
          <form className={styles.form} onSubmit={handlePasswordSubmit}>
            {emailIdentity}

            <div className={styles.identity}>
              <span className={styles.identityIcon}><StoreIcon /></span>
              <span className={styles.identityEmail}>{selectedTenant?.name ?? '—'}</span>
              {tenants.length > 1 && (
                <button
                  type="button"
                  className={styles.identityChange}
                  onClick={() => { setStep('tenant'); setPassword(''); setError(null); }}
                >
                  Cambiar
                </button>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Contraseña</label>
              <input
                className="input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <button
              className="btn btn--primary btn--full"
              type="submit"
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : 'Entrar'}
            </button>
          </form>
        )}

        <button
          type="button"
          className="btn btn--ghost btn--full"
          style={{ marginTop: 14, fontSize: 13 }}
          onClick={() => navigate('/hub')}
        >
          {hubUrl ? (hubName && hubName !== 'Caja principal' ? `Caja principal: ${hubName}` : 'Conectado a la caja principal') : 'Este aparato'}
        </button>
      </div>
    </div>
  );
}
